import React from "react";
import { ArrowUpRight, ArrowDownLeft, LogIn, LogOut, Lock, LockOpen } from "lucide-react";

interface ActionBarProps {
  /** Called when Send is clicked */
  onSend?: () => void;
  /** Called when Receive is clicked */
  onReceive?: () => void;
  /** Called when Portal In is clicked */
  onPortalIn?: () => void;
  /** Called when Portal Out is clicked */
  onPortalOut?: () => void;
  /** Called when Unlock is clicked */
  onUnlock?: () => void;
  /** Whether the portal animation is active (bridging in progress) */
  isPortalActive?: boolean;
  /** Whether private balances are unlocked */
  isPrivateUnlocked?: boolean;
}

/**
 * A horizontal action bar with 5 buttons: Send, Receive, Portal In, Portal Out, and Unlock.
 * Matches the pill-card design from the mockup.
 */
const ActionBar: React.FC<ActionBarProps> = ({
  onSend,
  onReceive,
  onPortalIn,
  onPortalOut,
  onUnlock,
  isPortalActive = false,
  isPrivateUnlocked = false,
}) => {
  const buttons = [
    {
      key: "send",
      label: "Send",
      icon: <ArrowUpRight className="w-6 h-6 text-purple-500 dark:text-purple-400" strokeWidth={2.5} />,
      onClick: onSend,
      active: false,
    },
    {
      key: "receive",
      label: "Receive",
      icon: <ArrowDownLeft className="w-6 h-6 text-purple-500 dark:text-purple-400" strokeWidth={2.5} />,
      onClick: onReceive,
      active: false,
    },
    {
      key: "portal-in",
      label: "Portal In",
      icon: <LogIn className="w-6 h-6 text-purple-500 dark:text-purple-400" strokeWidth={2.5} />,
      onClick: onPortalIn,
      active: false,
    },
    {
      key: "portal-out",
      label: "Portal Out",
      icon: <LogOut className="w-6 h-6 text-purple-500 dark:text-purple-400" strokeWidth={2.5} />,
      onClick: onPortalOut,
      active: false,
    },
    {
      key: "unlock",
      label: isPrivateUnlocked ? "Lock" : "Unlock",
      icon: isPrivateUnlocked ? (
        <LockOpen className="w-6 h-6 text-green-500 dark:text-green-400" strokeWidth={2.5} />
      ) : (
        <Lock className="w-6 h-6 text-purple-500 dark:text-purple-400" strokeWidth={2.5} />
      ),
      onClick: onUnlock,
      active: false,
    },
  ];

  return (
    <div className="grid grid-cols-5 gap-2 md:gap-4 w-full">
      {buttons.map((btn) => (
        <button
          key={btn.key}
          onClick={btn.onClick}
          className={`
            flex flex-col items-center justify-center gap-1.5 md:gap-2 px-2 md:px-8 py-3 md:py-4
            rounded-2xl border transition-all duration-200
            ${btn.active
              ? "bg-muted border-purple-500/40 portal-action-active"
              : "bg-muted/50 border-border hover:bg-muted hover:border-border"
            }
          `}
          aria-label={btn.label}
        >
          {btn.icon}
          <span className="text-foreground text-[10px] md:text-sm font-medium whitespace-nowrap">{btn.label}</span>
        </button>
      ))}
    </div>
  );
};

export default ActionBar;
